/**
 * t557u: "New channel with these members" - start a new channel from an existing channel's membership. Opens the
 * normal New channel form (web NewChannelModal, the app's NewChannelSheet) prefilled from
 * GET /conversations/:id/channel-template: its active members and their channel roles (owners and admins become admins
 * of the new channel; the creator is its owner), and its settings - private or public, section, building, access
 * (departments, groups, buildings), permissions (who can post, reply, react...), poll policy, forwarding,
 * acknowledgement due time, thread view and translation. Name and description start empty. Messages, pins, files,
 * integrations, extra role grants and the channel form (and its journey link) are never copied, so a form channel
 * gives a normal channel. Each person's own notification setting stays theirs. Saving goes through the normal create
 * endpoint and its checks. Shared by desktop web, phone web and the app.
 */
import type { ChannelRules } from './channelPermissions';
import { channelRulesForSave } from './channelPermissions';
import type { ChannelSettings } from './people';
import type { ChannelAccessUnits } from './readers';
import type { PollPolicy } from './polls';

export const COPY_MEMBERSHIP_LABEL = 'New channel with these members…';

export type ChannelMemberRole = 'owner' | 'admin' | 'member';
export interface ChannelTemplateMember { user_id: string; display_name: string; avatar_path?: string | null; role: ChannelMemberRole; /** false = deactivated or removed (the API leaves them out). */ active?: boolean }

/** GET /conversations/:id/channel-template */
export interface ChannelTemplate {
  source_id: string;
  source_slug: string | null;
  is_private: boolean;
  section_id: string | null;
  location_id: string | null;
  settings: ChannelSettings;
  poll_policy: PollPolicy;
  access: ChannelAccessUnits;
  permissions: ChannelRules;
  members: ChannelTemplateMember[];
  /** The source posts with a form; the new channel doesn't (choose one in Channel settings). */
  had_form: boolean;
}

/** What the New channel form starts with. */
export interface ChannelPrefill {
  isPrivate: boolean;
  /** Everyone else to add (the creator is always in, as the owner). */
  memberIds: string[];
  /** Of those, the ones who manage the source channel (they become admins of the new one). */
  managerIds: string[];
  sectionId: string | null;
  buildingId: string | null;
  access: ChannelAccessUnits;
  settings: ChannelSettings;
  pollPolicy: PollPolicy;
  permissions: ChannelRules;
}

const SETTING_KEYS: (keyof ChannelSettings)[] = ['auto_translate', 'threads_collapsed', 'ack_due_hours', 'forwarding'];

/** Only the channel-level defaults the form knows about. */
export function copyChannelSettings(s: ChannelSettings | null | undefined): ChannelSettings {
  const out: ChannelSettings = {};
  for (const k of SETTING_KEYS) if (s && s[k] !== undefined) (out as Record<string, unknown>)[k] = s[k];
  return out;
}

export function channelPrefill(t: ChannelTemplate, me: string | null | undefined): ChannelPrefill {
  const people = t.members.filter(m => m.active !== false && m.user_id !== me);
  return {
    isPrivate: t.is_private,
    memberIds: people.map(m => m.user_id),
    managerIds: people.filter(m => m.role === 'owner' || m.role === 'admin').map(m => m.user_id),
    sectionId: t.section_id ?? null,
    buildingId: t.location_id ?? null,
    access: { team_ids: [...(t.access?.team_ids ?? [])], location_ids: [...(t.access?.location_ids ?? [])] },
    settings: copyChannelSettings(t.settings),
    pollPolicy: t.poll_policy === 'moderators' ? 'moderators' : 'members',
    permissions: t.permissions ?? {},
  };
}

/** The extra create fields for a copied channel. Managers only count while they're still in the list. */
export function copiedChannelCreateFields(p: ChannelPrefill, memberIds: string[], permissions: ChannelRules = p.permissions) {
  const keep = new Set(memberIds);
  return {
    admin_ids: p.managerIds.filter(id => keep.has(id)),
    settings: p.settings,
    poll_policy: p.pollPolicy,
    permissions: channelRulesForSave(permissions),
  };
}

/** Shown in a channel's menu to people who can create channels and are in the channel (so they see its members). */
export function canCopyChannelMembership(c: { kind: string; is_member?: boolean | null } | null | undefined, canCreateChannels: boolean): boolean {
  return Boolean(c && canCreateChannels && c.kind === 'channel' && c.is_member);
}

/** "1 person", "42 people" */
export const peopleCountText = (n: number) => (n === 1 ? '1 person' : `${n} people`);
